import {
  NEW_LOOK_FIGURE_WEIGHT,
  NEW_LOOK_RADIUS,
} from "common/NewLook/tokens.ts";
import {
  PRICE_LADDER_ID,
  PRICE_LADDER_ZONE_ID,
  PRICE_LADDER_MARKER_ID,
  PRICE_LADDER_TICK_ID,
  PRICE_LADDER_PRICE_ID,
} from "testIds";
import React, { CSSProperties, useMemo } from "react";
import styled, { DefaultTheme, useTheme } from "styled-components";
import { Direction, LanguageNameSpaces, OrderStatus } from "@shared/types";
import { hexToRGB } from "@shared/utils/misc";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import useMeasure from "utils/hooks/useMeasure";
import { textWidth } from "common/NewLook/measureText.ts";
import TradeBar from "common/NewLook/TradeBar.tsx";
import useLadderPriceChange from "common/NewLook/useLadderPriceChange.tsx";
import { isTradeBarModel } from "common/NewLook/tradeBarLayout.ts";
import {
  formatFilledPercent,
  getLadderPlan,
  getLadderZones,
  getLadderX,
  GridLadderModel,
  isClusteredLadder,
  LadderPlan,
  LadderTick,
  LadderTone,
  LevelsLadderModel,
  PriceLadderModel,
  resolveTickOverlaps,
  SplitLadderModel,
} from "common/NewLook/priceLadder.ts";

export type PriceLadderSize = "compact" | "large";

interface PriceLadderProps {
  model: PriceLadderModel;
  /** compact: the bar alone (tables, phone cards); large: with labels. */
  size?: PriceLadderSize;
  formatPrice: (price: number) => string;
  /** The surface behind the ladder (the price dot's ring). */
  background?: string;
  showPrice?: boolean;
  showLabels?: boolean;
  priceChange?: number;
  className?: string;
  style?: CSSProperties;
}

const PriceLadder = ({
  model,
  size = "compact",
  formatPrice,
  background,
  showPrice,
  showLabels,
  priceChange,
  className,
  style,
}: PriceLadderProps) => {
  const theme = useTheme();
  const title = useLadderTitle(model, formatPrice);
  const bg = background ?? theme.colors.cardBgColor;
  if (model.type === "levels" && isTradeBarModel(model)) {
    const hasLabels = showLabels ?? true;
    return (
      <TradeBar
        model={model}
        formatPrice={formatPrice}
        background={bg}
        title={title}
        showLabels={hasLabels}
        showPrice={showPrice ?? hasLabels}
        priceChange={priceChange}
        toneColor={ladderToneColor}
        className={className}
        style={style}
      />
    );
  }
  if (model.type === "split") {
    return (
      <SplitLadder
        model={model}
        size={size}
        title={title}
        background={bg}
        className={className}
        style={style}
      />
    );
  }
  return (
    <AxisLadder
      model={model}
      size={size}
      title={title}
      background={bg}
      formatPrice={formatPrice}
      showPrice={showPrice}
      priceChange={priceChange}
      className={className}
      style={style}
    />
  );
};

export default React.memo(PriceLadder);

/* ------------------------------------------------------------------ */
/* Colours and sizes                                                   */
/* ------------------------------------------------------------------ */

/** Entries green, take-profits red, the stop orange, as on the TradingView chart. */
export const ladderToneColor = (theme: DefaultTheme, tone: LadderTone) => {
  switch (tone) {
    case "entry":
      return theme.colors.green;
    case "take_profit":
      return theme.colors.red;
    case "stop":
      return theme.colors.orange;
    default:
      return theme.colors.secondaryGrey;
  }
};
const trackColor = (theme: DefaultTheme) =>
  hexToRGB(theme.colors.secondaryGrey, 0.18);
const PENDING_ALPHA = 0.55;
const SPLIT_ALPHA = 0.8;
const tickColor = (theme: DefaultTheme, tick: LadderTick) => {
  const color = ladderToneColor(theme, tick.tone);
  return tick.filled ? color : hexToRGB(color, PENDING_ALPHA);
};
const dotShadow = (theme: DefaultTheme, background: string) =>
  `0 0 0 2px ${background}, 0 1px 3px 1px ${hexToRGB(
    theme.colors.secondaryGrey,
    0.35
  )}`;

const SIZES = {
  compact: { axis: 10, track: 4, tick: 10, dot: 8, cap: 6, gap: 4 },
  large: { axis: 14, track: 5, tick: 14, dot: 10, cap: 8, gap: 6 },
};
// Keeps the outermost ticks and the dot off the rounded ends.
const PADDING = { compact: 0.04, large: 0.02 };

/* ------------------------------------------------------------------ */
/* Price axis (levels / grid)                                          */
/* ------------------------------------------------------------------ */

interface AxisLadderProps {
  model: LevelsLadderModel | GridLadderModel;
  size: PriceLadderSize;
  title: string;
  background: string;
  formatPrice: (price: number) => string;
  showPrice?: boolean;
  priceChange?: number;
  className?: string;
  style?: CSSProperties;
}

const AxisLadder = ({
  model,
  size,
  title,
  background,
  formatPrice,
  showPrice,
  priceChange,
  className,
  style,
}: AxisLadderProps) => {
  const theme = useTheme();
  const { t: tBots } = useTypedTranslation(LanguageNameSpaces.BOTS);
  const change = useLadderPriceChange(priceChange);
  const isLarge = size === "large";
  const sizes = SIZES[size];
  const padding = PADDING[size];
  const plan = useMemo(
    () => getLadderPlan(model, { nearGridLines: isLarge ? 3 : 2 }),
    [model, isLarge]
  );
  const [measureRef, measured] = useMeasure<HTMLDivElement | null>();
  const width = measured?.width || 0;
  const ticks = useMemo(
    () => resolveTickOverlaps(plan, width, padding, sizes.gap),
    [plan, width, padding, sizes.gap]
  );
  const x = (price: number) => getLadderX(plan, price, padding) * 100;

  const labels = useMemo(
    () =>
      isLarge && width
        ? placeLabels(plan, ticks, model, width, padding, formatPrice, tBots)
        : [],
    [isLarge, width, plan, ticks, model, padding, formatPrice, tBots]
  );
  const marker = plan.marker;
  const zones = model.type === "levels" ? getLadderZones(model) : [];
  const markerColor = marker?.isOutOfRange
    ? theme.colors.orange
    : theme.colors.secondaryText;
  const chip =
    isLarge && width && marker && !marker.isClose
      ? placeChip(
          getLadderX(plan, marker.price, padding) * width,
          formatPrice(marker.price),
          width
        )
      : undefined;
  const priceTag =
    !isLarge && showPrice && width && marker && !marker.isClose
      ? placeChip(
          getLadderX(plan, marker.price, padding) * width,
          formatPrice(marker.price),
          width,
          PRICE_TAG_PADDING,
          change.width
        )
      : undefined;
  const lanes = labels.reduce((max, label) => Math.max(max, label.lane + 1), 0);
  // Price-only labels (a grid's range) need one line per lane.
  const laneHeight = labels.some((label) => label.title && label.price)
    ? LANE_HEIGHT
    : PRICE_LANE_HEIGHT;

  return (
    <Wrapper
      ref={measureRef}
      title={title}
      className={className}
      style={style}
      data-testid={PRICE_LADDER_ID}
      data-direction={
        model.type === "levels" ? (model.isShort ? "short" : "long") : undefined
      }
    >
      {chip && (
        <ChipRow>
          <Chip
            style={{ left: chip.left }}
            $outOfRange={!!marker?.isOutOfRange}
          >
            {chip.text}
          </Chip>
        </ChipRow>
      )}
      <Axis style={{ height: sizes.axis }}>
        <Track style={{ height: sizes.track, background: trackColor(theme) }}>
          {zones.map((zone) => {
            const from = x(zone.from);
            const to = x(zone.to);
            if (to <= from) return null;
            return (
              <Fill
                key={zone.tone}
                data-testid={PRICE_LADDER_ZONE_ID}
                data-tone={zone.tone}
                style={{
                  left: `${from}%`,
                  width: `${to - from}%`,
                  background: hexToRGB(ladderToneColor(theme, zone.tone), 0.65),
                }}
              />
            );
          })}
        </Track>
        {marker &&
          (marker.isClose ? (
            <Centered
              data-testid={PRICE_LADDER_MARKER_ID}
              style={{
                left: `${x(marker.price)}%`,
                width: sizes.cap,
                height: sizes.cap,
                borderRadius: 1.5,
                background: theme.colors.secondaryText,
                boxShadow: `0 0 0 1.5px ${background}`,
              }}
            />
          ) : (
            <Centered
              data-testid={PRICE_LADDER_MARKER_ID}
              style={{
                left: `${x(marker.price)}%`,
                width: sizes.dot,
                height: sizes.dot,
                borderRadius: "50%",
                background: markerColor,
                boxShadow: dotShadow(theme, background),
              }}
            />
          ))}
        {ticks.map((tick) => (
          <Centered
            key={tick.key}
            data-testid={PRICE_LADDER_TICK_ID}
            data-tone={tick.tone}
            style={{
              left: `${x(tick.price)}%`,
              width: 2,
              height: sizes.tick,
              borderRadius: 1,
              background: tickColor(theme, tick),
            }}
          />
        ))}
      </Axis>
      {priceTag && (
        <PriceTag
          style={{ left: priceTag.left }}
          $outOfRange={!!marker?.isOutOfRange}
        >
          <span data-testid={PRICE_LADDER_PRICE_ID}>{priceTag.text}</span>
          {change.element}
        </PriceTag>
      )}
      {lanes > 0 && (
        <Labels style={{ height: lanes * laneHeight }}>
          {labels.map((label) => (
            <Label
              key={label.key}
              style={{
                ...LABEL_ANCHORS[label.align](label.center),
                top: label.lane * laneHeight,
                textAlign: label.align,
              }}
            >
              {label.title && (
                <LabelTitle style={{ color: toneText(theme, label.tone) }}>
                  {label.title}
                </LabelTitle>
              )}
              {label.price && <LabelPrice>{label.price}</LabelPrice>}
            </Label>
          ))}
        </Labels>
      )}
    </Wrapper>
  );
};

const toneText = (theme: DefaultTheme, tone: LadderTone) =>
  tone === "neutral"
    ? theme.colors.secondaryText
    : ladderToneColor(theme, tone);

/* ---- large labels ---- */

const LANE_HEIGHT = 34;
const CHIP_PADDING = 14;
const PRICE_TAG_PADDING = 2;
const PRICE_LANE_HEIGHT = 16;
const LABEL_GAP = 8;
const LABEL_ANCHORS: Record<
  PlacedLabel["align"],
  (center: number) => CSSProperties
> = {
  left: () => ({ left: 0 }),
  center: (center) => ({ left: center, transform: "translateX(-50%)" }),
  right: () => ({ right: 0 }),
};

interface PlacedLabel {
  key: string;
  title?: string;
  price?: string;
  tone: LadderTone;
  center: number;
  lane: number;
  align: "left" | "center" | "right";
}

/**
 * Lays the labels (E1, TP2, SL, Avg, a DCA's "E19–E50"… with their prices)
 * under their ticks in up to two lanes, most important first; a label that
 * fits in neither is left to the tooltip.
 */
const placeLabels = (
  plan: LadderPlan,
  ticks: LadderTick[],
  model: LevelsLadderModel | GridLadderModel,
  width: number,
  padding: number,
  formatPrice: (price: number) => string,
  tBots: (key: "backtesting.labels.avg") => string
): PlacedLabel[] => {
  const candidates: {
    key: string;
    title?: string;
    price?: string;
    tone: LadderTone;
    at: number;
    priority: number;
  }[] = ticks
    .filter((tick) =>
      model.type === "grid"
        ? tick.role === "bound"
        : tick.label || tick.role === "average"
    )
    .map((tick) => ({
      key: tick.key,
      title:
        tick.role === "average" ? tBots("backtesting.labels.avg") : tick.label,
      price: formatPrice(tick.price),
      tone: tick.tone,
      at: tick.price,
      priority: tick.priority,
    }));
  plan.bandLabels.forEach((band) =>
    candidates.push({
      key: band.key,
      title: band.title,
      tone: "entry",
      at: (band.from + band.to) / 2,
      priority: 0,
    })
  );
  candidates.sort((a, b) => b.priority - a.priority || a.at - b.at);
  const lanes: [number, number][][] = [[], []];
  const placed: PlacedLabel[] = [];
  candidates.forEach(({ key, title, price, tone, at }) => {
    const labelWidth =
      Math.max(textWidth(title || ""), textWidth(price || "")) + 2;
    const center = getLadderX(plan, at, padding) * width;
    let left = center - labelWidth / 2;
    let align: PlacedLabel["align"] = "center";
    if (left < 0) {
      left = 0;
      align = "left";
    } else if (left + labelWidth > width) {
      left = width - labelWidth;
      align = "right";
    }
    const right = left + labelWidth;
    const lane = lanes.findIndex((intervals) =>
      intervals.every(
        ([start, end]) => right + LABEL_GAP <= start || left >= end + LABEL_GAP
      )
    );
    if (lane < 0) return;
    lanes[lane].push([left, right]);
    placed.push({ key, title, price, tone, center, lane, align });
  });
  return placed;
};

const placeChip = (
  center: number,
  text: string,
  width: number,
  padding = CHIP_PADDING,
  suffixWidth = 0
) => {
  const chipWidth = textWidth(text) + padding;
  const left = Math.min(
    Math.max(center - chipWidth / 2, 0),
    width - chipWidth - suffixWidth
  );
  return { left, text };
};

/* ------------------------------------------------------------------ */
/* Hedging bots: long / short split                                    */
/* ------------------------------------------------------------------ */

interface SplitLadderProps {
  model: SplitLadderModel;
  size: PriceLadderSize;
  title: string;
  background: string;
  className?: string;
  style?: CSSProperties;
}

const SplitLadder = ({
  model,
  size,
  title,
  background,
  className,
  style,
}: SplitLadderProps) => {
  const theme = useTheme();
  const { t } = useTypedTranslation(LanguageNameSpaces.BOTS);
  const isLarge = size === "large";
  const sizes = SIZES[size];
  const isShortLeg = model.side === Direction.SHORT;
  const known = model.longShare !== undefined;
  const longShare = known ? model.longShare! : isShortLeg ? 0 : 1;
  // Both legs are open positions: equal weight, split by a 2px gap.
  const parts = [
    {
      key: "long",
      share: longShare,
      color: theme.colors.green,
      own: !isShortLeg,
    },
    {
      key: "short",
      share: 1 - longShare,
      color: theme.colors.red,
      own: isShortLeg,
    },
  ];
  return (
    <Wrapper
      title={title}
      className={className}
      style={style}
      data-testid={PRICE_LADDER_ID}
    >
      <Axis style={{ height: sizes.axis }}>
        <Track style={{ height: sizes.track, background: trackColor(theme) }}>
          {parts.map((part, index) =>
            part.share > 0 ? (
              <Fill
                key={part.key}
                style={{
                  left: index === 0 ? 0 : `${longShare * 100}%`,
                  width: `${part.share * 100}%`,
                  background: hexToRGB(part.color, SPLIT_ALPHA),
                }}
              />
            ) : null
          )}
        </Track>
        {known && longShare > 0 && longShare < 1 && (
          <Centered
            style={{
              left: `${longShare * 100}%`,
              width: 2,
              height: sizes.track + 2,
              background,
            }}
          />
        )}
      </Axis>
      {isLarge && (
        <SplitLabels>
          {parts.map((part) =>
            known || part.own ? (
              <SplitLabel key={part.key}>
                <LabelTitle style={{ color: part.color }}>
                  {part.key === "long" ? t("labels.long") : t("labels.short")}
                </LabelTitle>
                {known && (
                  <LabelPrice>{Math.round(part.share * 100)}%</LabelPrice>
                )}
              </SplitLabel>
            ) : (
              <span key={part.key} />
            )
          )}
        </SplitLabels>
      )}
    </Wrapper>
  );
};

/* ------------------------------------------------------------------ */
/* Tooltip                                                             */
/* ------------------------------------------------------------------ */

const statusText = (filled: number, status?: string) =>
  status ||
  (filled >= 1
    ? OrderStatus.FULFILLED
    : filled > 0
    ? OrderStatus.PARTIALLY_FULFILLED
    : OrderStatus.PENDING);
const percent = (value: number) => `${Math.round(value * 100)}%`;

/** Every level on its own line, for the ladder's native tooltip. */
const useLadderTitle = (
  model: PriceLadderModel,
  formatPrice: (price: number) => string
) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.TRADES);
  const { t: tBots } = useTypedTranslation(LanguageNameSpaces.BOTS);
  return useMemo(() => {
    const lines: string[] = [];
    const current = (price?: number) => {
      if (price !== undefined)
        lines.push(`${t("statistics.current_price")} · ${formatPrice(price)}`);
    };
    if (model.type === "split") {
      lines.push(
        model.longShare !== undefined
          ? `${tBots("labels.long")} ${percent(model.longShare)} · ${tBots(
              "labels.short"
            )} ${percent(1 - model.longShare)}`
          : model.side === Direction.SHORT
          ? tBots("labels.short")
          : tBots("labels.long")
      );
      return lines.join("\n");
    }
    if (model.type === "grid") {
      lines.push(
        `${tBots("labels.price_range")} · ${formatPrice(
          model.min
        )} – ${formatPrice(model.max)}`
      );
      if (model.lines?.length)
        lines.push(`${tBots("labels.grids")} · ${model.lines.length}`);
      current(model.currentPrice);
      return lines.join("\n");
    }
    const entries = model.levels.filter((level) => level.kind === "entry");
    const takeProfits = model.levels.filter(
      (level) => level.kind === "take_profit"
    );
    const stops = model.levels.filter((level) => level.kind === "stop");
    const orderLine = (
      name: string,
      level: (typeof entries)[number],
      index?: number
    ) =>
      `${name}${
        index !== undefined ? ` ${level.number ?? index + 1}` : ""
      } · ${formatPrice(level.price)} · ${statusText(
        level.filled,
        level.status
      )}`;
    if (model.isComplete) {
      if (isClusteredLadder(model)) {
        const filled = entries.filter((level) => level.filled > 0).length;
        lines.push(
          `${tBots("labels.entries")} ${filled}/${
            entries.length
          } · ${formatPrice(entries[0].price)} – ${formatPrice(
            entries[entries.length - 1].price
          )}`
        );
      } else {
        entries.forEach((level, index) =>
          lines.push(orderLine(t("orders.entry_title"), level, index))
        );
      }
      if (model.averageEntry !== undefined)
        lines.push(
          `${t("card.average_entry_price")} · ${formatPrice(
            model.averageEntry
          )}`
        );
      takeProfits.forEach((level, index) =>
        lines.push(orderLine(t("orders.take_profit_title"), level, index))
      );
      stops.forEach((level) =>
        lines.push(orderLine(tBots("labels.stop"), level))
      );
    } else {
      if (model.progress) {
        lines.push(
          `${t("orders.entry_title")} ${formatFilledPercent(
            model.progress.entry
          )}`,
          `${t("orders.take_profit_title")} ${formatFilledPercent(
            model.progress.takeProfit
          )}`
        );
      }
      if (entries[0])
        lines.push(
          `${tBots("labels.next_entry")} · ${formatPrice(entries[0].price)}`
        );
      if (takeProfits[0])
        lines.push(
          `${t("table.headers.next_take_profit")} · ${formatPrice(
            takeProfits[0].price
          )}`
        );
      if (stops[0])
        lines.push(`${tBots("labels.stop")} · ${formatPrice(stops[0].price)}`);
      if (model.averageEntry !== undefined)
        lines.push(
          `${t("card.average_entry_price")} · ${formatPrice(
            model.averageEntry
          )}`
        );
    }
    if (model.closePrice !== undefined)
      lines.push(
        `${t("drawer.status_closed")} · ${formatPrice(model.closePrice)}${
          model.closeStatus ? ` · ${model.closeStatus}` : ""
        }`
      );
    current(model.currentPrice);
    return lines.join("\n");
  }, [model, formatPrice, t, tBots]);
};

/* ------------------------------------------------------------------ */
/* Styles                                                              */
/* ------------------------------------------------------------------ */

const Wrapper = styled.div`
  position: relative;
  width: 100%;
  min-width: 0;
`;
const Axis = styled.div`
  position: relative;
`;
const Track = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
  overflow: hidden;
  border-radius: ${(p) => p.theme.radii.pill}px;
`;
const Fill = styled.span`
  position: absolute;
  top: 0;
  bottom: 0;
`;
const Centered = styled.span`
  position: absolute;
  top: 50%;
  transform: translate(-50%, -50%);
  pointer-events: none;
`;
const PriceTag = styled.span<{ $outOfRange: boolean }>`
  position: absolute;
  bottom: calc(100% + 3px);
  color: ${(p) =>
    p.$outOfRange ? p.theme.colors.orange : p.theme.colors.titleColor};
  font-size: 11.5px;
  line-height: 14px;
  font-weight: ${NEW_LOOK_FIGURE_WEIGHT};
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
  pointer-events: none;
`;
const ChipRow = styled.div`
  position: relative;
  height: 20px;
  margin-bottom: 4px;
`;
const Chip = styled.span<{ $outOfRange: boolean }>`
  position: absolute;
  top: 0;
  padding: 1px 7px;
  border-radius: ${NEW_LOOK_RADIUS.chip}px;
  border: ${(p) => p.theme.borders.lightest};
  background: ${(p) => p.theme.colors.cardBgColor};
  color: ${(p) =>
    p.$outOfRange ? p.theme.colors.orange : p.theme.colors.titleColor};
  font-size: 11.5px;
  line-height: 16px;
  font-weight: ${(p) => p.theme.typography.fontWeights.MEDIUM};
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
`;
const Labels = styled.div`
  position: relative;
  margin-top: 6px;
`;
const Label = styled.span`
  position: absolute;
  display: flex;
  flex-direction: column;
  white-space: nowrap;
  line-height: 15px;
`;
const LabelTitle = styled.span`
  font-size: 11.5px;
  font-weight: ${(p) => p.theme.typography.fontWeights.MEDIUM};
`;
const LabelPrice = styled.span`
  color: ${(p) => p.theme.colors.secondaryText};
  font-size: 11.5px;
  font-variant-numeric: tabular-nums;
`;
const SplitLabels = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 12px;
  margin-top: 6px;
`;
const SplitLabel = styled.span`
  display: inline-flex;
  align-items: baseline;
  gap: 6px;
  line-height: 16px;
  white-space: nowrap;
`;
